"use client";

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  iFinePrintBlockData,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";

interface FinePrintSectionProps {
  heading?: string;
  gallery?: string[];
  finePrintData?: iFinePrintBlockData;
}

export function FinePrintSection({
  heading = "Fine Print",
  gallery = [],
  finePrintData,
}: FinePrintSectionProps) {
  const items = finePrintData?.items ?? [];
  const previewCount = finePrintData?.previewCount ?? 5;
  const [expanded, setExpanded] = useState(false);
  const bannerImage = gallery[0];
  const visibleItems =
    expanded || items.length <= previewCount
      ? items
      : items.slice(0, previewCount);
  const canExpand = items.length > previewCount;

  return (
    <section
      className="bg-white p-4 sm:p-6 space-y-4 sm:space-y-5"
      style={{
        borderRadius: quoteThemeCssVars.radiusCard,
        boxShadow: quoteThemeCssVars.shadowCard,
      }}
    >
      <h2
        className="text-xl sm:text-2xl font-semibold"
        style={{
          color: quoteThemeCssVars.blockHeading,
          fontFamily: quoteThemeCssVars.headingFont,
        }}
      >
        {heading}
      </h2>

      {bannerImage ? (
        <div className="overflow-hidden rounded-xl">
          <img
            src={bannerImage}
            alt=""
            className="w-full h-40 sm:h-52 object-cover"
          />
        </div>
      ) : null}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500">No fine print added</p>
      ) : (
        <ul className="space-y-3">
          {visibleItems.map((item) => (
            <li key={item.id} className="flex items-start gap-3">
              <span
                className="mt-1.5 h-2 w-2 rotate-45 flex-shrink-0"
                style={quoteThemeColorStyles.primaryBg}
                aria-hidden
              />
              <span className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {item.text}
              </span>
            </li>
          ))}
        </ul>
      )}

      {canExpand ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-center gap-1 rounded-xl px-4 py-3 text-sm font-semibold text-white"
          style={quoteThemeColorStyles.primaryButton}
        >
          {expanded ? "Show less" : "View all"}
          {!expanded ? <ChevronRight className="h-4 w-4" /> : null}
        </button>
      ) : null}
    </section>
  );
}
